import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchWatchesRequest } from 'Redux/Slices';
import { RootState } from 'Redux/Bootstrap';
import { useCountry } from 'Context/CountryContext';
import WatchCard from 'View/Component/WatchCard/WatchCard';
import * as S from './LatestWatchesSlider.style';

const LatestWatchesSlider: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { country } = useCountry();
  const { watches, loading } = useSelector((state: RootState) => state.watch);

  const sliderRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, dragged: false });

  useEffect(() => {
    dispatch(fetchWatchesRequest({ take: 5, page: 1 }));
  }, [dispatch, country, i18n.language]);

  // Mouse drag-to-scroll (desktop). Touch swipe works natively via overflow-x: auto.
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = sliderRef.current;
    if (!el) return;
    // Prevent native image/text drag so mousedown on a card still starts our drag
    e.preventDefault();
    dragState.current.isDown = true;
    dragState.current.dragged = false;
    dragState.current.startX = e.pageX - el.offsetLeft;
    dragState.current.scrollLeft = el.scrollLeft;
    // Disable snap + smooth immediately at the DOM level — React state would lag
    el.style.scrollBehavior = 'auto';
    el.style.scrollSnapType = 'none';
  };

  const endDrag = () => {
    if (!dragState.current.isDown) return;
    dragState.current.isDown = false;
    const el = sliderRef.current;
    if (el) {
      // Re-enable snap so cards rest at a clean position; smooth makes the snap feel polished
      el.style.scrollBehavior = '';
      el.style.scrollSnapType = '';
    }
  };

  const handleMouseLeave = () => endDrag();
  const handleMouseUp = () => endDrag();

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragState.current.isDown || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = x - dragState.current.startX;
    if (Math.abs(walk) > 5) {
      dragState.current.dragged = true;
    }
    // Direct scroll — no React state in the hot path
    sliderRef.current.scrollLeft = dragState.current.scrollLeft - walk;
  };

  // Swallow click after a drag so card navigation doesn't fire on accidental drag
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragState.current.dragged) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.dragged = false;
    }
  };

  const latestWatches = watches.slice(0, 5);

  if (loading && latestWatches.length === 0) {
    return null;
  }

  return (
    <S.Section>
      <S.Container>
        <S.SectionTitle>{t('watches.latest_title')}</S.SectionTitle>

        <S.SliderContainer
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onClickCapture={handleClickCapture}
          data-testid="latest-watches-slider"
        >
          {latestWatches.map((watch: any) => (
            <S.CardWrapper key={watch.id}>
              <WatchCard watch={watch} />
            </S.CardWrapper>
          ))}

          <S.ViewAllCard onClick={() => navigate('/kaufen')}>
            <S.ViewAllIcon>→</S.ViewAllIcon>
            <S.ViewAllText>{t('watches.view_all')}</S.ViewAllText>
          </S.ViewAllCard>
        </S.SliderContainer>
      </S.Container>
    </S.Section>
  );
};

export default LatestWatchesSlider;
